
import { create } from 'zustand';
import { odooClient } from '@/services/odoo-client';

// ============ Notification Types ============
export interface AppNotification {
  id: string;
  type: 'ticket_new' | 'ticket_updated' | 'message_new' | 'stage_changed' | 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  ticketId?: number;
  duration?: number;
  timestamp: number;
  read: boolean;
}

interface NotificationState {
  notifications: AppNotification[];
  unreadCount: number;
  isPolling: boolean;
  isOpen: boolean;
  lastPollId: number;
  addNotification: (n: Omit<AppNotification, 'id' | 'timestamp' | 'read'>) => void;
  removeNotification: (id: string) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  clearAll: () => void;
  setOpen: (open: boolean) => void;
  startPolling: () => void;
  stopPolling: () => void;
  requestBrowserPermission: () => Promise<boolean>;
}

let pollInterval: ReturnType<typeof setInterval> | null = null;
let lastNotificationId = 0;

// ============ Browser Notification Helper ============
async function showBrowserNotification(notification: AppNotification) {
  if (!('Notification' in window)) return;
  if (Notification.permission !== 'granted') return;

  try {
    const browserNotif = new Notification(notification.title, {
      body: notification.message || '',
      icon: '/favicon.ico',
      tag: notification.id,
      badge: '/favicon.ico',
    });
    setTimeout(() => browserNotif.close(), 5000);
  } catch (e) {
    console.warn('Browser notification failed:', e);
  }
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  isPolling: false,
  isOpen: false,
  lastPollId: 0,

  addNotification: (n) => {
    const notification: AppNotification = {
      ...n,
      id: `notif-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      timestamp: Date.now(),
      read: false,
    };
    set((state) => {
      const updated = [notification, ...state.notifications].slice(0, 100);
      return {
        notifications: updated,
        unreadCount: updated.filter((x) => !x.read).length,
      };
    });

    // Show browser notification
    showBrowserNotification(notification);

    // Auto-dismiss after duration (0 = persistent)
    if (n.duration !== 0) {
      setTimeout(() => {
        get().removeNotification(notification.id);
      }, n.duration || 8000);
    }
  },

  removeNotification: (id) =>
    set((state) => {
      const updated = state.notifications.filter((n) => n.id !== id);
      return {
        notifications: updated,
        unreadCount: updated.filter((x) => !x.read).length,
      };
    }),

  markAsRead: (id) =>
    set((state) => {
      const updated = state.notifications.map((n) =>
        n.id === id ? { ...n, read: true } : n
      );
      return {
        notifications: updated,
        unreadCount: updated.filter((x) => !x.read).length,
      };
    }),

  markAllAsRead: () =>
    set((state) => ({
      notifications: state.notifications.map((n) => ({ ...n, read: true })),
      unreadCount: 0,
    })),

  clearAll: () => set({ notifications: [], unreadCount: 0 }),

  setOpen: (open) => set({ isOpen: open }),

  startPolling: () => {
    if (pollInterval) return;
    if (!odooClient.isConnected) return;
    if (get().isPolling) return;

    set({ isPolling: true });

    const poll = async () => {
      if (!odooClient.isConnected) return;

      try {
        // Check for new messages in the last 60 seconds
        const recentMessages = await odooClient.searchRead(
          'mail.message',
          [
            ['model', '=', 'helpdesk.ticket'],
            ['date', '>', new Date(Date.now() - 60000).toISOString().replace('T', ' ').replace(/\.\d{3}Z$/, '')]
          ],
          ['id', 'subject', 'body', 'author_id', 'date', 'res_id'],
          10, 0, 'date desc'
        );

        const state = get();

        // Process new messages
        for (const msg of recentMessages) {
          if (msg.id <= state.lastPollId) continue;

          const authorName = msg.author_id ? msg.author_id[1] : 'نظام';
          const cleanBody = msg.body ? msg.body.replace(/<[^>]*>/g, '').trim().substring(0, 80) : '';

          get().addNotification({
            type: 'message_new',
            title: `رسالة جديدة من ${authorName}`,
            message: cleanBody || 'مرفق',
            ticketId: msg.res_id,
            duration: 0, // persistent until user dismisses
          });

          if (msg.id > lastNotificationId) {
            lastNotificationId = msg.id;
          }
        }

        set({ lastPollId: lastNotificationId });
      } catch (e) {
        console.warn('Notification poll failed:', e);
      }
    };

    poll();
    pollInterval = setInterval(poll, 30000);
  },

  stopPolling: () => {
    if (pollInterval) {
      clearInterval(pollInterval);
      pollInterval = null;
    }
    set({ isPolling: false });
  },

  requestBrowserPermission: async () => {
    if (!('Notification' in window)) return false;
    if (Notification.permission === 'granted') return true;
    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    return false;
  },
}));
